import { useState, createElement } from 'react';
import { DragStartEvent, DragEndEvent, DropAnimation } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useMoveAssetsToGroup } from '@/hooks/api/reports/useMoveAssetsToGroup';
import { useToast } from '@/hooks/useToast';
import { Asset } from '@/api/generatedApi';
import { useDndSensors, getRectCenter } from '@/components/dnd';
import { useReportDrag } from './ReportDragContext';
import { DragPreview } from './DragPreview';

interface TransformValues {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
}

const createReportDropAnimation = (translateX: number, translateY: number): DropAnimation => ({
  duration: 320,
  easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  keyframes: ({ transform }) => {
    const initial = (transform.initial ?? transform) as TransformValues;
    const final: TransformValues = {
      x: translateX,
      y: translateY,
      scaleX: initial.scaleX * 0.85,
      scaleY: initial.scaleY * 0.85,
    };

    return [
      { transform: CSS.Transform.toString(initial), opacity: 0.92 },
      { transform: CSS.Transform.toString(final), opacity: 0 },
    ];
  },
});

export const useReportDnd = (reportId: string | undefined) => {
  const showToast = useToast(state => state.showToast);
  const { gridApiReference } = useReportDrag();

  const { mutate: moveAssets } = useMoveAssetsToGroup({
    reportId: reportId || '',
    onSuccess: () => {
      showToast({ message: 'Assets moved successfully', type: 'success' });
    },
    onError: (error: string) => {
      showToast({ message: error, type: 'error' });
    },
  });

  const [draggedData, setDraggedData] = useState<{ count: number; assets: Asset[] } | undefined>();
  const [dropAnimation, setDropAnimation] = useState<DropAnimation | undefined>();

  const sensors = useDndSensors();

  const handleDragStart = (event: DragStartEvent) => {
    setDropAnimation(undefined);
    const dragData = event.active.data.current as { count: number; rows: Asset[] } | undefined;
    if (dragData) {
      setDraggedData({ count: dragData.count, assets: dragData.rows });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const dragData = active.data.current as { ids: string[]; sourceGroupId: string; count: number } | undefined;
    const dropData = over?.data.current as { type: string; groupId: string } | undefined;

    const isValidDrop = !!(over && dropData?.type === 'report-group' && dragData?.sourceGroupId !== dropData?.groupId);

    if (isValidDrop && over.rect && active.rect.current) {
      const initialActiveRect = 'initial' in active.rect.current ? active.rect.current.initial : active.rect.current;

      if (initialActiveRect) {
        const activeCenter = getRectCenter(initialActiveRect);
        const targetCenter = getRectCenter(over.rect);

        const horizontalOffset = over.rect.width / 2;
        const translateX = targetCenter.x - activeCenter.x - horizontalOffset;
        const translateY = targetCenter.y - activeCenter.y;

        if (Number.isFinite(translateX) && Number.isFinite(translateY)) {
          setDropAnimation(createReportDropAnimation(translateX, translateY));
        }
      }
    }

    setDraggedData(undefined);
    if (!isValidDrop) setDropAnimation(undefined);

    if (!over || !dragData || active.id === over.id) return;

    if (isValidDrop) {
      gridApiReference.current?.setRowSelectionModel({ type: 'include', ids: new Set() });
      moveAssets({
        assetIds: dragData.ids,
        sourceGroupId: dragData.sourceGroupId,
        targetGroupId: dropData.groupId,
      });
    }
  };

  const dragOverlay =
    draggedData === undefined
      ? undefined
      : createElement(DragPreview, { count: draggedData.count, assets: draggedData.assets });

  return {
    sensors,
    handleDragStart,
    handleDragEnd,
    dragOverlay,
    isDragging: draggedData !== undefined,
    dropAnimation,
  };
};
